/**
 * StickersApi over fetch (contracts routes: stickers, sticker-packs, gifs/config) with the session's access token:
 * refreshed before it expires, and once more after a 401. Writes with a clientId send it as the Idempotency-Key.
 */
import type {
  GifConfigResponse,
  MediaSettingsResponse,
  MyStickersResponse,
  StickerLibraryResponse,
  StickerPackResponse,
  StickerResponse,
} from "@bundle/contracts";
import { buildPath, routes, type RouteName } from "@bundle/contracts/routes";
import { ApiFailure, type ApiErrorKind, type SessionViewModel, type StickersApi } from "@bundle/core";
import { API_BASE } from "../env.ts";

function kindFor(status: number): ApiErrorKind {
  if (status === 401) return "auth";
  if (status === 403) return "forbidden";
  if (status === 404) return "notFound";
  if (status === 429) return "rateLimited";
  if (status >= 400 && status < 500) return "invalid";
  return status === 502 || status === 503 || status === 504 ? "network" : "server";
}

export function createStickersApi(session: SessionViewModel, base: string = API_BASE): StickersApi {
  async function call<T>(name: RouteName, params: Record<string, string>, body?: unknown, idempotencyKey?: string): Promise<T> {
    const route = routes[name];
    const url = base + buildPath(route.path, params);
    const send = async (token: string | undefined) => {
      const headers: Record<string, string> = { accept: "application/json" };
      if (body !== undefined) headers["content-type"] = "application/json";
      if (token !== undefined) headers.authorization = `Bearer ${token}`;
      if (idempotencyKey !== undefined) headers["idempotency-key"] = idempotencyKey;
      try {
        return await fetch(url, { method: route.method, headers, body: body === undefined ? undefined : JSON.stringify(body) });
      } catch (e) {
        throw new ApiFailure("network", 0, "network", e instanceof Error ? e.message : "network error");
      }
    };
    await session.ensureFreshToken();
    let token = session.state.session?.token;
    let res = await send(token);
    if (res.status === 401 && token !== undefined && (await session.authRejected(token))) {
      token = session.state.session?.token;
      res = await send(token);
    }
    if (res.status === 204) return undefined as T;
    const text = await res.text();
    let json: unknown;
    try {
      json = text === "" ? undefined : JSON.parse(text);
    } catch {
      json = undefined;
    }
    if (!res.ok) {
      const err = (json as { error?: { code?: string; message?: string } } | undefined)?.error;
      throw new ApiFailure(kindFor(res.status), res.status, err?.code ?? String(res.status), err?.message ?? res.statusText);
    }
    return json as T;
  }

  return {
    library: (ws) => call<StickerLibraryResponse>("stickers", { ws }),
    pack: async (ws, packId) => (await call<StickerPackResponse>("getStickerPack", { ws, packId })).pack,
    createPack: async (ws, req) => (await call<StickerPackResponse>("createStickerPack", { ws }, req, req.clientId)).pack,
    updatePack: async (ws, packId, req) => (await call<StickerPackResponse>("updateStickerPack", { ws, packId }, req)).pack,
    deletePack: (ws, packId) => call<void>("deleteStickerPack", { ws, packId }),
    createSticker: async (ws, packId, req) => (await call<StickerResponse>("createSticker", { ws, packId }, req, req.clientId)).sticker,
    deleteSticker: (ws, stickerId) => call<void>("deleteSticker", { ws, stickerId }),
    setMyPacks: async (ws, packIds) => (await call<MyStickersResponse>("updateMyStickers", { ws }, { packIds })).myPackIds,
    updateSettings: async (ws, req) => (await call<MediaSettingsResponse>("updateMediaSettings", { ws }, req)).settings,
    gifConfig: (ws) => call<GifConfigResponse>("gifConfig", { ws }),
  };
}
